import { mayLeavePage } from "../navigation/leaveGuard";
import type { NavigateFunction } from "react-router-dom";
import { agentToolCatalog } from "../../autogen/agentToolCatalog";
import {
  AgentAuditOutcome,
  ContractStateDto,
  DeadlineSubject,
  type EventListItemDto,
  OrganizationType,
} from "../../autogen/bff-api";
import { urls } from "../../urls";
import type {
  AgentToolDescriptor,
  AgentTurn,
  AuditedValues,
  ConfirmDecision,
  DocumentSearchScope,
  DocumentSource,
  EntityHighlight,
  IndexedEntityKind,
  RiskTier,
} from "./agentTypes";
import type { PendingConfirmation } from "./ConfirmationCard";
import { currentToolName } from "./retiredToolNames";
import {
  addEntityHighlight,
  highlightFromMutationResult,
  highlightFromReadResult,
  isPlainRecord,
} from "./entityHighlights";
import { TOOL_MODULE_NOT_INCLUDED, TOOL_MODULE_NOT_INCLUDED_MESSAGE, toolModuleNotIncluded } from "./enabledTools";
import { AI_NOT_ALLOWED, AI_NOT_ALLOWED_NOTE } from "../ai/aiNotAllowed";
import {
  DRAFT_TOOL_NAME,
  OPEN_DOCUMENT_TOOL_NAME,
  SEARCH_TOOL_NAME,
  checkProposal,
  runTool,
  toError,
  validateToolArgs,
} from "./tools";
import { PROCUREMENT_RULES_TOOL_NAME, procurementRulesFor } from "./procurementRules";
import {
  percentageWeightsOf,
  refusedUserValues,
  subCriterionWeightGapsOf,
  weightDepartures,
  weightPartsOf,
  weightSumOf,
  withHonestUserSources,
} from "./requestDepartures";
import { otherTotals, statesOtherTotal, withoutOtherTotals } from "./statedWeightTotal";
import { criterionLabel } from "./confirmationCardFormatting";
import { type Withheld, savedWithoutNote, withheldFieldsOf, withoutHiddenFields } from "./wizardHiddenFields";
import {
  readCurrent,
  readStoredEvent,
  withChangesSinceRead,
  withExpectedCurrent,
  withStateAfterRefusal,
} from "./proposalBasis";
import { statesBeforeValueNotOnCard, withCardBeforeValues } from "./statedBeforeValues";
import { namesProcedureOfOtherPart, withoutOtherStoredPart, withoutProcedureOfOtherPart } from "./statedProcedure";
import { withStoredRulesAfterSave } from "./storedRules";
import { filledInValues, isMissingValue, missingValues } from "./filledInValues";
import type { MissingValue } from "./filledInValues";
import { removalsOnCard, removesOrRenames } from "./cardRemovals";
import type { CardRemovals } from "./cardRemovals";
import { sourcingEventForModel } from "./sourcingEventForModel";
import { bffApi } from "../../autogen/bff-api";
import i18next from "i18next";
import type { GenerateDraft, RefusalReason, ToolContext, ToolResult } from "./tools";
import { unknownFieldsRefusal } from "./unknownToolFields";
import { changesNothingRefusal, withJsonTextDecoded, wrongShapeRefusal } from "./toolArgShape";
import { CREATED_FROM_NEED } from "./originatingNeed";
import { withAwardCriterionDescriptionsAsHtml } from "./richTextArgs";
import type { AuditProps } from "./useAgentAudit";
import type { DocumentSearch } from "./useDocumentSearch";
import { CREATES_PROCUREMENT_PARAM, CREATES_PROCUREMENT_VALUE, PROCUREMENT_PARAM } from "./createProcurementParams";

export type StepOutcome = "stream" | "aborted";

// The effect a single executed call has on the turn: its result (always fed back to the planner), an
// optional outcome transition (null leaves the current outcome untouched), and whether it ends the
// tool loop.
export type CallResult = { result: ToolResult; outcome: StepOutcome | null; breakOuter: boolean };

export type CallDeps = {
  ctx: ToolContext;
  entityIndex: Map<string, Record<string, unknown>>;
  navigate: NavigateFunction;
  navigateToEvent: (event: EventListItemDto) => void;
  searchDocuments: DocumentSearch;
  openDocument: (documentId: string, name: string) => Promise<{ result: ToolResult; source: DocumentSource | null }>;
  generateDraft: GenerateDraft;
  signal?: AbortSignal;
  recordAudit: (outcome: AgentAuditOutcome, p: AuditProps) => void;
  waitForConfirmation: (pending: PendingConfirmation) => Promise<ConfirmDecision>;
  setActiveStep: (label: string | null) => void;
  updateTurn: (patch: (turn: AgentTurn) => AgentTurn) => void;
  turnIndex: number;
  userInstruction: string;
  seenCalls: Set<string>;
  refusedProposals: string[];
  refusedUserValues: RefusalReason[];
  missingValues: MissingValue[];
  askedAboutRemovals: string[];
  askedAboutWeightTotal: string[];
  classificationsConfirmed?: string[];
  userWords: string;
  enabledTools: ReadonlySet<string> | null;
  withheldByAiSetting?: ReadonlySet<string>;
};

const WITHHELD_BY_AI_SETTING: ToolResult = { ok: false, deviationCode: AI_NOT_ALLOWED, message: AI_NOT_ALLOWED_NOTE };

const DECLINED: ToolResult = { ok: false, deviationCode: "USER_DECLINED", message: "Brukeren avviste handlingen." };

const SUPERSEDED: ToolResult = {
  ok: false,
  deviationCode: "USER_WROTE_INSTEAD",
  message:
    "Handlingen er IKKE utført: brukeren svarte med en ny melding i stedet for å bekrefte. Gjør det den nye " +
    "meldingen ber om, og foreslå ikke denne handlingen på nytt med mindre den nye meldingen ber om den.",
};

export const procurementBeingCreatedHere = (
  id: string,
  { pathname, search }: { pathname: string; search: string } = window.location,
): string | null => {
  const query = new URLSearchParams(search);
  if (pathname !== urls.app.assistant || query.get(CREATES_PROCUREMENT_PARAM) !== CREATES_PROCUREMENT_VALUE) return null;
  query.set(PROCUREMENT_PARAM, id);
  return `${pathname}?${query.toString()}`;
};

// A successfully created entity whose page the assistant should open right away, keyed by tool.
const RESULT_NAV: Record<string, ((id: string) => string) | undefined> = {
  createSourcingEvent: (id) => {
    const here = procurementBeingCreatedHere(id);
    if (here) return here;
    const { pathname, search } = urls.app.sourcingEvents.edit.go(id).info;
    return `${pathname}?${search}`;
  },
  createProject: (id) => urls.projects.view.go(id),
};

// The only fact resolveEntityNavigation is allowed to route on: an explicit tag this module stamped
// on an entity at index time. Never re-derived from an entity's own fields downstream — see
// resolveEntityNavigation's doc comment for why (EventRevisionDto vs ContractDto collide on `state`).
const AGENT_ENTITY_KIND = "__agentEntityKind";

// ContractDto and ContractListItemDto are the only response shapes with a bare (no `contractId`
// sibling) `id` that combine a `state: ContractStateDto` with a required top-level `currency`.
// EventRevisionDto — nested inside SourcingEventDto.changes and walked by indexEntities' array
// recursion — also has `id` + `state` (its EventRevisionState values literally overlap
// ContractStateDto's: "Draft"/"Published"), but never a top-level `currency`, so it's excluded here
// rather than mis-tagged as a contract.
const isBareContractShape = (obj: Record<string, unknown>): boolean =>
  typeof obj.state === "string" && typeof obj.currency === "string";

// A list item carries a top-level `title` — the one shape every other consumer (navigation, card
// label) is written against. `getSourcingEvent` nests its title under `eventData`, so tag a copy onto
// that same shape here and nothing downstream has to special-case it. Returns null for anything that
// isn't recognizably a sourcing event.
const asIndexedEvent = (obj: Record<string, unknown>): Record<string, unknown> | null => {
  const eventData = obj.eventData;
  if (isPlainRecord(eventData) && typeof eventData.title === "string") {
    return { ...obj, title: eventData.title, [AGENT_ENTITY_KIND]: "v2Event" };
  }
  return null;
};

// The one org payload a declared tool kind can't cover: searchOrganizationsAndEntries returns a
// MIXED list, so each hit carries its own explicit type discriminator instead. Full-profile reads
// route via their tool's declared kind.
const asOrganizationKind = (obj: Record<string, unknown>): IndexedEntityKind | null => {
  if (obj.type === OrganizationType.Organization) {
    return "organization";
  }
  if (obj.type === OrganizationType.OrganizationEntry) {
    return "orgEntry";
  }
  return null;
};

const indexEntity = (
  obj: Record<string, unknown>,
  into: Map<string, Record<string, unknown>>,
  declaredKind: IndexedEntityKind | undefined,
): void => {
  const event = asIndexedEvent(obj);
  if (event && typeof event.id === "string") {
    into.set(event.id, event);
    return;
  }
  if (typeof obj.id === "string") {
    const kind = declaredKind ?? asOrganizationKind(obj) ?? (isBareContractShape(obj) ? "contract" : null);
    if (kind) {
      into.set(obj.id, { ...obj, [AGENT_ENTITY_KIND]: kind });
    } else if (!into.get(obj.id)?.[AGENT_ENTITY_KIND]) {
      into.set(obj.id, obj);
    }
  }
  if (typeof obj.bidId === "string") {
    into.set(obj.bidId, obj);
  }
  if (typeof obj.contractId === "string" && typeof obj.state === "string") {
    into.set(obj.contractId, { ...obj, [AGENT_ENTITY_KIND]: "contract" });
  }
  const counterpartyKind = asOrganizationKind({ type: obj.counterpartyType });
  if (typeof obj.counterpartyId === "string" && counterpartyKind) {
    indexUnlessKnown(into, obj.counterpartyId, counterpartyKind);
  }
};

// Exported for direct unit testing of the tagging rules above — indexEntity/asIndexedEvent are the
// part of the pipeline actually under test; runAndIndex just wires this to a live tool call.
// declaredKind is the producing tool's entity kind (server-owned, from the allowlist): it applies to
// the result's primary entities — the root object, or the items of a list wrapper — never to
// collections nested INSIDE an entity (an org's contactPersons are not orgs).
export const indexEntities = (
  data: unknown,
  into: Map<string, Record<string, unknown>>,
  declaredKind?: IndexedEntityKind,
): void => {
  if (Array.isArray(data)) {
    data.forEach((item) => indexEntities(item, into, declaredKind));
    return;
  }
  if (!data || typeof data !== "object") {
    return;
  }
  const obj = data as Record<string, unknown>;
  indexEntity(obj, into, declaredKind);
  const nestedKind = typeof obj.id === "string" ? undefined : declaredKind;
  Object.values(obj).forEach((value) => {
    if (Array.isArray(value)) {
      indexEntities(value, into, nestedKind);
    }
  });
};

const runAndIndex = async (deps: CallDeps, callName: string, args: Record<string, unknown>): Promise<ToolResult> => {
  const result = await runTool(callName, args, deps.ctx, deps.generateDraft, deps.signal);
  if (result.ok) {
    if (callName === DEADLINES_TOOL_NAME) {
      indexDeadlines(result.data, deps.entityIndex);
    } else {
      const entityKind = agentToolCatalog.find((tool) => tool.toolName === callName)?.entityKind;
      indexEntities(result.data, deps.entityIndex, entityKind);
      indexOriginatingNeed(result.data, deps.entityIndex);
    }
  }
  return result;
};

const indexOriginatingNeed = (data: unknown, into: Map<string, Record<string, unknown>>): void => {
  const need = isPlainRecord(data) ? data[CREATED_FROM_NEED] : undefined;
  if (isPlainRecord(need) && typeof need.needId === "string") {
    indexEntities({ id: need.needId, title: need.title }, into, "need");
  }
};

export const DEADLINES_TOOL_NAME = "listMyUpcomingDeadlines";

export const indexDeadlines = (data: unknown, into: Map<string, Record<string, unknown>>): void => {
  const deadlines = isPlainRecord(data) && Array.isArray(data.deadlines) ? data.deadlines : [];
  deadlines.filter(isPlainRecord).forEach((deadline) => {
    const { subject, subjectId, parentId } = deadline;
    if (typeof subjectId !== "string") {
      return;
    }
    if (subject === DeadlineSubject.SourcingEventV2) {
      indexUnlessKnown(into, subjectId, "sourcingEvent");
    } else if (subject === DeadlineSubject.Contract) {
      indexUnlessKnown(into, subjectId, "contract");
    } else if (subject === DeadlineSubject.ContractTask && typeof parentId === "string") {
      into.set(subjectId, { id: subjectId, contractId: parentId, [AGENT_ENTITY_KIND]: "contractTask" });
      indexUnlessKnown(into, parentId, "contract");
    }
  });
};

const indexUnlessKnown = (into: Map<string, Record<string, unknown>>, id: string, kind: IndexedEntityKind): void => {
  if (!into.get(id)?.[AGENT_ENTITY_KIND]) {
    into.set(id, { id, [AGENT_ENTITY_KIND]: kind });
  }
};

export type EntityNavigationDeps = Pick<CallDeps, "entityIndex" | "navigate" | "navigateToEvent">;

// The routing rule for an indexed entity id, shared by the planner's own "navigate" tool call and the
// entity highlight cards a turn renders. Dispatches ONLY on the `__agentEntityKind` tag indexEntity
// stamped at index time — never by re-inspecting the entity's own fields (an EventRevisionDto and a
// ContractDto both carry `id` + a `state` string that can literally be "Draft"/"Published"; guessing
// from shape alone routed a revision to a contract 404). Fails closed — returns null, never a guess —
// for an id the index has never seen or an entity indexEntity chose not to tag, so callers never route
// to a 404 on a type we don't actually recognize.
export const resolveEntityNavigation = (id: string, deps: EntityNavigationDeps): (() => void) | null => {
  if (!id) {
    return null;
  }
  const entity = deps.entityIndex.get(id);
  if (!entity) {
    return null;
  }
  const kind = entity[AGENT_ENTITY_KIND];
  if (kind === "v2Event") {
    return () => deps.navigateToEvent(entity as unknown as EventListItemDto);
  }
  if (kind === "sourcingEvent") {
    return () => deps.navigate(urls.app.sourcingEvents.view.go(id));
  }
  if (kind === "contractTask") {
    const contractId = entity.contractId;
    return typeof contractId === "string" ? () => deps.navigate(urls.app.contracts.view.task(contractId, id)) : null;
  }
  if (kind === "organization") {
    return () => deps.navigate(urls.app.organizationProfile.go(id));
  }
  if (kind === "orgEntry") {
    return () => deps.navigate(urls.app.organizationProfile.goToEntry(id));
  }
  if (kind === "project") {
    return () => deps.navigate(urls.app.projects.view.go(id));
  }
  if (kind === "need") {
    return () => deps.navigate(urls.app.needs.view.go(id));
  }
  if (kind === "me") {
    return () => deps.navigate(urls.app.profile);
  }
  if (kind === "notification") {
    return () => deps.navigate(urls.app.notifications);
  }
  if (kind !== "contract") {
    return null;
  }
  const contractId = typeof entity.contractId === "string" ? entity.contractId : id;
  return () =>
    deps.navigate(
      entity.state === ContractStateDto.Draft && mayEditContract(entity)
        ? urls.contracts.edit.go(contractId).details
        : urls.contracts.viewSingle.go(contractId),
    );
};

const mayEditContract = (contract: Record<string, unknown>): boolean =>
  contract.canUpdate === true || contract.canEditEverything === true;

// A highlight card is only worth rendering if clicking it actually goes somewhere — see
// resolveEntityNavigation's fail-closed contract. Dropping the ones that don't resolve is strictly
// better than rendering a card that 404s.
export const navigableHighlight = (
  highlight: EntityHighlight | null,
  deps: EntityNavigationDeps,
): EntityHighlight | null => (highlight && resolveEntityNavigation(highlight.id, deps) ? highlight : null);

const SEARCH_STEP_LABEL = "Søker i dokumentene";
const OPEN_DOCUMENT_STEP_LABEL = "Henter dokumentet";
const PROCUREMENT_RULES_STEP_LABEL = "Henter regelverk, prosedyrer og frister";

const sourceKey = (source: DocumentSource): string => source.memoryId ?? source.documentId ?? source.documentName;

const mergeSources = (existing: DocumentSource[], added: DocumentSource[]): DocumentSource[] => {
  const seen = new Set(existing.map(sourceKey));
  return [...existing, ...added.filter((source) => !seen.has(sourceKey(source)))];
};

const openDocumentCall = async (args: Record<string, unknown>, deps: CallDeps): Promise<CallResult> => {
  const documentId = String(args.documentId ?? "");
  const name = String(args.name ?? "");
  deps.setActiveStep(OPEN_DOCUMENT_STEP_LABEL);
  const { result, source } = await deps.openDocument(documentId, name);
  deps.updateTurn((turn) => ({
    ...turn,
    steps: [...turn.steps, { label: OPEN_DOCUMENT_STEP_LABEL, ok: result.ok }],
    sources: source ? mergeSources(turn.sources ?? [], [source]) : turn.sources,
  }));
  return { result, outcome: null, breakOuter: false };
};

const procurementRulesCall = async (args: Record<string, unknown>, deps: CallDeps): Promise<CallResult> => {
  deps.setActiveStep(PROCUREMENT_RULES_STEP_LABEL);
  const result = await readProcurementRules(String(args.eventId ?? ""), deps);
  deps.updateTurn((turn) => ({
    ...turn,
    steps: [...turn.steps, { label: PROCUREMENT_RULES_STEP_LABEL, ok: result.ok }],
  }));
  return { result, outcome: null, breakOuter: false };
};

const readProcurementRules = async (eventId: string, { ctx, entityIndex }: CallDeps): Promise<ToolResult> => {
  try {
    const event = await ctx.dispatch(bffApi.endpoints.getSourcingEvent.initiate({ eventId })).unwrap();
    indexEntities(event, entityIndex);
    return { ok: true, data: procurementRulesFor(event, i18next.t, new Date(), ctx.buyerRunsPublicProcurements, ctx.buyerRegulation) };
  } catch (err) {
    return toError(err);
  }
};

const searchCall = async (args: Record<string, unknown>, deps: CallDeps): Promise<CallResult> => {
  const query = String(args.query ?? "");
  const scope: DocumentSearchScope = args.scope === "page" ? "page" : args.scope === "bids" ? "bids" : "organization";
  const callKey = `${SEARCH_TOOL_NAME}:${JSON.stringify({ query, scope })}`;
  if (deps.seenCalls.has(callKey)) {
    return { result: { ok: true, data: { note: "Samme søk allerede utført." } }, outcome: null, breakOuter: false };
  }
  deps.setActiveStep(SEARCH_STEP_LABEL);
  const { result, sources } = await deps.searchDocuments(query, scope);
  if (result.ok) {
    deps.seenCalls.add(callKey);
  }
  if (!result.ok && result.deviationCode === TOOL_MODULE_NOT_INCLUDED) {
    return { result, outcome: null, breakOuter: false };
  }
  const refusedBySetting = !result.ok && result.deviationCode === AI_NOT_ALLOWED;
  deps.updateTurn((turn) => ({
    ...turn,
    steps: [...turn.steps, { label: refusedBySetting ? AI_NOT_ALLOWED_NOTE : SEARCH_STEP_LABEL, ok: result.ok }],
    sources: mergeSources(turn.sources ?? [], sources),
  }));
  return { result, outcome: null, breakOuter: false };
};

const OPENED_PAGE_STEP = "Åpnet siden";
const PAGE_NOT_FOUND_STEP = "Fant ikke siden å åpne";
const STAYED_ON_PAGE_STEP = "Ble på siden, endringer er ikke lagret";
const STAYED_ON_PAGE = {
  navigated: false,
  note:
    "Siden ble ikke åpnet: brukeren har endringer på denne siden som ikke er lagret, og blir spurt om dem nå. " +
    "Ikke si at siden er åpnet. Si kort at den ikke ble åpnet, og hvorfor.",
};
const OPENED_PAGE = {
  navigated: true,
  note:
    "Siden er åpnet for brukeren, og turen fortsetter. Gjør resten av det brukeren ba om, hvis noe gjenstår. " +
    "Gjenstår ingenting, svar med én kort setning om hva som er åpnet.",
};

const navigateCall = (args: Record<string, unknown>, deps: CallDeps): CallResult => {
  const targetId = typeof args.id === "string" ? args.id : "";
  const go = targetId ? resolveEntityNavigation(targetId, deps) : null;
  const hasTarget = go !== null || (typeof args.route === "string" && args.route !== "");
  if (hasTarget && !mayLeavePage()) {
    deps.updateTurn((turn) => ({
      ...turn,
      steps: [...turn.steps, { label: STAYED_ON_PAGE_STEP, ok: false }],
    }));
    return { result: { ok: true, data: STAYED_ON_PAGE }, outcome: null, breakOuter: false };
  }
  let navigated = true;
  if (go) {
    go();
  } else if (typeof args.route === "string" && args.route) {
    deps.navigate(args.route);
  } else {
    navigated = false;
  }
  deps.updateTurn((turn) => ({
    ...turn,
    steps: [...turn.steps, { label: navigated ? OPENED_PAGE_STEP : PAGE_NOT_FOUND_STEP, ok: navigated }],
  }));
  return { result: { ok: true, data: navigated ? OPENED_PAGE : { navigated } }, outcome: null, breakOuter: false };
};

const gatedMutationCall = async (
  input: {
    callName: string;
    args: Record<string, unknown>;
    label: string;
    riskTier: RiskTier;
    descriptor: AgentToolDescriptor;
    plannerSummary: string | null;
  },
  deps: CallDeps,
): Promise<CallResult> => {
  const { callName, label, riskTier, descriptor, plannerSummary } = input;
  const decoded = withAwardCriterionDescriptionsAsHtml(withJsonTextDecoded(descriptor, input.args));
  const translateEnum = (type: string) => (i18next.exists(type) ? String(i18next.t(type)) : undefined);
  const labelOf = (criterion: Record<string, unknown>) => criterionLabel(criterion, translateEnum);
  const departuresFrom = (proposed: Record<string, unknown>, current?: Record<string, unknown>) =>
    weightDepartures(descriptor, proposed, deps.userInstruction, labelOf, current);
  const args = withHonestUserSources(descriptor, decoded, deps.userWords, departuresFrom(decoded));
  const audit = (
    outcome: AgentAuditOutcome,
    auditedArgs: Record<string, unknown>,
    deviationCode?: string,
    values?: AuditedValues,
  ) =>
    deps.recordAudit(outcome, {
      toolName: callName,
      riskTier,
      args: auditedArgs,
      turnId: deps.turnIndex,
      userInstruction: deps.userInstruction,
      deviationCode,
      values,
    });

  const refusal =
    unknownFieldsRefusal(descriptor, args) ??
    wrongShapeRefusal(descriptor, args) ??
    (callName === UPDATE_SOURCING_EVENT ? createWithAiRefusal(bodyOf(descriptor, args), deps) : null) ??
    changesNothingRefusal(descriptor, args) ??
    validateToolArgs(callName, args);
  if (refusal) {
    return { result: refusal, outcome: null, breakOuter: false };
  }
  const check = descriptor.checksBeforeConfirm ? await checkProposal(callName, args, deps.ctx) : null;
  if (check?.refused) {
    deps.refusedProposals.push(callName);
    deps.missingValues.push(...missingValues(callName, descriptor, args, check.reasons));
    const userValues = refusedUserValues(
      descriptor,
      args,
      check.reasons.filter((reason) => !isMissingValue(descriptor, args, reason)),
    );
    deps.refusedUserValues.push(...userValues);
    if (deps.refusedProposals.length <= SILENT_CORRECTIONS_PER_TURN) {
      const result = userValues.length > 0 ? userValueNotSaveable(check.result) : notShownToUser(check.result);
      return { result: await withStoredStateIfChanged(result, args, deps), outcome: null, breakOuter: false };
    }
  }
  const weightSum = weightSumOf(descriptor, args);
  if (!check?.refused && weightSum !== null && weightSum !== 100 && deps.refusedProposals.length < SILENT_CORRECTIONS_PER_TURN) {
    deps.refusedProposals.push(callName);
    return { result: weightsNotAddingUp(weightSum), outcome: null, breakOuter: false };
  }
  const subWeightGaps = check?.refused ? [] : subCriterionWeightGapsOf(descriptor, args);
  if (subWeightGaps.length > 0 && deps.refusedProposals.length < SILENT_CORRECTIONS_PER_TURN) {
    deps.refusedProposals.push(callName);
    const gaps = subWeightGaps.map(({ criterion, unweighted, sum }) => ({ label: labelOf(criterion), unweighted, sum }));
    return { result: subCriterionWeightsNotAddingUp(gaps), outcome: null, breakOuter: false };
  }
  const refusedReasons: RefusalReason[] | undefined = check?.refused ? check.reasons : undefined;
  const departsFromRequest = deps.refusedUserValues.length > 0 ? [...deps.refusedUserValues] : undefined;
  const basis = descriptor.checksBeforeConfirm ? await readCurrent(callName, args, deps.ctx) : null;
  if (basis && !basis.ok) {
    return { result: basis, outcome: null, breakOuter: false };
  }
  const current = basis?.ok ? (basis.data as Record<string, unknown>) : undefined;
  const withheld = withheldFieldsOf(descriptor, args, current);
  if (withheld && deps.refusedProposals.length < SILENT_CORRECTIONS_PER_TURN) {
    deps.refusedProposals.push(callName);
    return { result: withheld.refusal, outcome: null, breakOuter: false };
  }
  const visible = withheld ? withoutHiddenFields(descriptor, args, withheld.fields) : args;
  const departures = departuresFrom(visible, current);
  const proposal = withHonestUserSources(descriptor, visible, deps.userWords, departures);
  const filledIn = filledInValues(callName, descriptor, proposal, deps.missingValues);
  if (withheld && changesNothingRefusal(descriptor, proposal)) {
    return { result: withheld.refusal, outcome: null, breakOuter: false };
  }
  const removals = current ? removalsOnCard(bodyOf(descriptor, proposal), current) : null;
  if (current && removals && removesOrRenames(removals) && !deps.askedAboutRemovals.includes(callName)) {
    deps.askedAboutRemovals.push(callName);
    return { result: removesOrRenamesUnasked(removals, current), outcome: null, breakOuter: false };
  }
  const cardTotal = weightSumOf(descriptor, proposal);
  const card = cardTotal === null ? null : { total: cardTotal, parts: weightPartsOf(descriptor, proposal) };
  let summary = plannerSummary;
  if (card && plannerSummary && statesOtherTotal(plannerSummary, card)) {
    deps.updateTurn((turn) => ({
      ...turn,
      answerText: turn.answerText ? withoutOtherTotals(turn.answerText, card) || null : turn.answerText,
    }));
    if (!deps.askedAboutWeightTotal.includes(callName)) {
      deps.askedAboutWeightTotal.push(callName);
      const weights = (percentageWeightsOf(descriptor, proposal) ?? []).map(
        ({ criterion, weight }) => ({ label: labelOf(criterion), weight }),
      );
      const result = totalUnlikeCard(otherTotals(plannerSummary, card), card.total, weights, current);
      return { result, outcome: null, breakOuter: false };
    }
    summary = withoutOtherTotals(plannerSummary, card) || null;
  }
  const cardBody = bodyOf(descriptor, proposal);
  if (statesBeforeValueNotOnCard(summary, cardBody, current) || namesProcedureOfOtherPart(summary, cardBody, current)) {
    summary = null;
  }
  const classified = callName === CONFIRM_CLASSIFICATION ? await readStoredEvent(args, deps.ctx) : undefined;
  if (summary && classified) {
    summary = withoutOtherStoredPart(summary, classified) || null;
  }
  deps.updateTurn((turn) => {
    if (!turn.answerText) return turn;
    const procedureChecked = withoutProcedureOfOtherPart(
      withCardBeforeValues(turn.answerText, cardBody, current, translateEnum),
      cardBody,
      current,
    );
    const answerText = classified ? withoutOtherStoredPart(procedureChecked, classified) : procedureChecked;
    return answerText === turn.answerText ? turn : { ...turn, answerText: answerText || null };
  });
  audit(AgentAuditOutcome.Proposed, proposal);
  const decision = await deps.waitForConfirmation({
    label,
    riskTier,
    descriptor,
    args: proposal,
    summary,
    ...(filledIn.length > 0 ? { filledIn } : {}),
    ...(refusedReasons ? { refusedReasons } : {}),
    ...(departsFromRequest ? { departsFromRequest } : {}),
    ...(departures.length > 0 ? { weightDepartures: departures } : {}),
    ...(current ? { current } : {}),
  });
  if (decision.kind === "supersede") {
    audit(AgentAuditOutcome.Rejected, proposal);
    deps.updateTurn((turn) => ({ ...turn, note: "Justerer forslaget …" }));
    return { result: SUPERSEDED, outcome: "aborted", breakOuter: true };
  }
  if (decision.kind === "cancel") {
    audit(AgentAuditOutcome.Rejected, proposal);
    deps.updateTurn((turn) => ({ ...turn, steps: [...turn.steps, { label: `${label} – avbrutt`, ok: false }] }));
    const declined = check?.refused
      ? await withStoredStateIfChanged(closedAsRefused(check.result), args, deps, current)
      : DECLINED;
    return { result: declined, outcome: null, breakOuter: true };
  }
  audit(AgentAuditOutcome.Confirmed, decision.finalArgs, undefined, decision.values);
  deps.setActiveStep(label);
  const sent = current ? withExpectedCurrent(descriptor, decision.finalArgs, current) : decision.finalArgs;
  const outcome = await runAndIndex(deps, callName, sent);
  const result = outcome.ok
    ? withWithheldNoted(withRemovalsSaved(savedWithStoredRules(callName, descriptor, sent, outcome), removals), withheld)
    : savedNothingIfRefused(await withStateAfterRefusal(callName, descriptor, sent, outcome, deps.ctx));
  const mutationHighlight = highlightFromMutationResult(result, decision.finalArgs, deps.entityIndex);
  const highlight = navigableHighlight(mutationHighlight, deps);
  deps.updateTurn((turn) => ({
    ...turn,
    steps: [...turn.steps, { label, ok: result.ok }],
    entities: addEntityHighlight(turn.entities ?? [], highlight),
  }));
  audit(
    result.ok ? AgentAuditOutcome.Executed : AgentAuditOutcome.Failed,
    decision.finalArgs,
    result.ok ? undefined : result.deviationCode,
  );
  if (result.ok && callName === CONFIRM_CLASSIFICATION) {
    deps.classificationsConfirmed?.push(String(args.eventId ?? ""));
  }
  const navTo = RESULT_NAV[callName];
  const created = navTo && result.ok ? (result.data as { id?: unknown }) : undefined;
  if (navTo && typeof created?.id === "string") {
    deps.navigate(navTo(created.id));
  }
  return { result, outcome: "stream", breakOuter: true };
};

const SILENT_CORRECTIONS_PER_TURN = 2;

const CONFIRM_CLASSIFICATION = "confirmSourcingEventClassification";

const withStoredStateIfChanged = async (
  refused: ToolResult,
  args: Record<string, unknown>,
  deps: CallDeps,
  alreadyRead?: Record<string, unknown>,
): Promise<ToolResult> => {
  const lastRead = typeof args.eventId === "string" ? deps.entityIndex.get(args.eventId) : undefined;
  if (refused.ok || !isPlainRecord(lastRead?.eventContext)) return refused;
  const now = alreadyRead ?? (await readStoredEvent(args, deps.ctx));
  if (!now) return refused;
  const result = withChangesSinceRead(refused, lastRead, now);
  if (result !== refused) indexEntities(now, deps.entityIndex);
  return result;
};

const savedWithStoredRules = (
  callName: string,
  descriptor: AgentToolDescriptor,
  sent: Record<string, unknown>,
  saved: ToolResult,
): ToolResult => (callName === UPDATE_SOURCING_EVENT ? withStoredRulesAfterSave(saved, bodyOf(descriptor, sent)) : saved);

const UPDATE_SOURCING_EVENT = "updateSourcingEvent";

const bodyOf = (descriptor: AgentToolDescriptor, args: Record<string, unknown>): Record<string, unknown> => {
  const name = descriptor.params.find((param) => param.in === "body")?.name;
  const body = name ? args[name] : undefined;
  return body && typeof body === "object" && !Array.isArray(body) ? (body as Record<string, unknown>) : {};
};

const CONFIRMED_ON_CARD: ToolResult = {
  ok: false,
  deviationCode: "CONFIRMED_ON_CARD",
  message:
    "Ikke lagret, og ikke vist brukeren: i «Opprett med KI» bekrefter brukeren regelverk og prosedyre selv, på " +
    "kortet på skjermen. Sett aldri publicContext.regulation, publicContext.foaPart eller " +
    "publicContext.procedureType her, og spør ikke om dem. Still ikke neste spørsmål; vent på brukerens melding.",
};

const AWAITS_REGULATION_ON_CARD: ToolResult = {
  ok: false,
  deviationCode: "AWAITS_REGULATION_ON_CARD",
  message:
    "Ikke lagret, og ikke vist brukeren: brukeren har ennå ikke bekreftet regelverk og prosedyre på kortet på " +
    "skjermen. Kvalifikasjonskrav, tildelingskriterier, minstekrav, frister og invitasjoner kommer etter det. Still " +
    "ikke neste spørsmål; vent på brukerens melding om at regelverk og prosedyre er bekreftet.",
};

const SET_ON_REGULATION_CARD = ["regulation", "foaPart", "procedureType"];

const AFTER_REGULATION: Record<string, string[]> = {
  eventData: [
    "selectionCriteria",
    "awardCriteria",
    "minimumRequirements",
    "bidSubmissionDeadline",
    "bidSubmissionDeadlineConfirmed",
    "requestDeadline",
    "requestDeadlineConfirmed",
    "milestonePlan",
  ],
  privateContext: ["organizationInvites"],
  miniCompetitionContext: ["organizationInvites"],
};

const setsAnyOf = (part: unknown, fields: string[]): boolean =>
  isPlainRecord(part) && fields.some((field) => field in part);

const createWithAiRefusal = (body: Record<string, unknown>, { ctx, classificationsConfirmed }: CallDeps): ToolResult | null => {
  if (!ctx.createsProcurementWithAi) return null;
  if (setsAnyOf(body.publicContext, SET_ON_REGULATION_CARD)) return CONFIRMED_ON_CARD;
  const writesAfterRegulation = Object.entries(AFTER_REGULATION).some(([part, fields]) => setsAnyOf(body[part], fields));
  const regulationPending = (classificationsConfirmed?.length ?? 0) > 0 || !!ctx.regulationPending?.();
  return writesAfterRegulation && regulationPending ? AWAITS_REGULATION_ON_CARD : null;
};

const removalsInWords = (removals: CardRemovals): string =>
  [
    ...(removals.removed.length > 0 ? [`fjerner ${removals.removed.map((name) => `«${name}»`).join(", ")}`] : []),
    ...removals.renamed.map((rename) => `endrer navnet «${rename.from}» til «${rename.to}»`),
  ].join(", og ");

const removesOrRenamesUnasked = (removals: CardRemovals, current: Record<string, unknown>): ToolResult => ({
  ok: false,
  deviationCode: "CARD_REMOVES_OR_RENAMES",
  message:
    `Ikke vist brukeren: sammenlignet med slik anskaffelsen er lagret nå («current»), ${removalsInWords(removals)}. ` +
    "Ba brukeren bare om noe annet, for eksempel vekter, bygger du forslaget på nytt nå ut fra «current», så " +
    "bare det brukeren ba om endres og alt annet står som det er lagret. Ba brukeren om det, foreslår du det samme " +
    "igjen, og teksten over kortet sier rett ut hva kortet fjerner eller gir nytt navn. Skriv aldri at noe " +
    "«beholdes» som kortet endrer.",
  current: sourcingEventForModel(current),
});

const withWithheldNoted = (saved: ToolResult, withheld: Withheld | null): ToolResult =>
  saved.ok && withheld && saved.data && typeof saved.data === "object"
    ? { ok: true, data: { savedWithoutFields: savedWithoutNote(withheld.fields), ...(saved.data as Record<string, unknown>) } }
    : saved;

const withRemovalsSaved = (saved: ToolResult, removals: CardRemovals | null): ToolResult =>
  saved.ok && removals && removesOrRenames(removals) && saved.data && typeof saved.data === "object"
    ? {
        ok: true,
        data: {
          savedRemovalsAndRenames:
            `Lagringen ${removalsInWords(removals)}. Si det i svaret, ikke bare hva som står igjen.`,
          ...(saved.data as Record<string, unknown>),
        },
      }
    : saved;

const totalUnlikeCard = (
  stated: number[],
  total: number,
  weights: { label: string; weight: number }[],
  current: Record<string, unknown> | undefined,
): ToolResult => ({
  ok: false,
  deviationCode: "CARD_TOTAL_UNLIKE_TEXT",
  message:
    `Ikke vist brukeren: teksten over kortet oppgir en sum på ${stated.map((sum) => `${sum} %`).join(" og ")}, ` +
    `men vektene på kortet summerer til ${total} % (${weights.map(({ label, weight }) => `«${label}» ${weight} %`).join(", ")}). ` +
    "Foreslå kortet på nytt nå, med en tekst over kortet som oppgir summen kortet har og nevner hvert kriterium " +
    "brukeren ikke nevnte med vekten det har på kortet, eller som ikke oppgir noen sum. Skriv aldri en sum kortet " +
    "ikke har.",
  ...(current ? { current: sourcingEventForModel(current) } : {}),
});

export const NOTHING_SAVED = "Ingenting ble lagret.";

const notShownToUser = (refused: Extract<ToolResult, { ok: false }>): ToolResult => ({
  ...refused,
  message:
    "Ikke vist brukeren: systemet ville ikke lagret dette forslaget, og brukeren har ikke sett det. " +
    `Grunnen: ${refused.message} Rett forslaget ut fra grunnen og foreslå det på nytt nå, i samme tur, ` +
    "uten å spørre brukeren. Ta med alt det forrige forslaget skulle gjøre.",
});

const userValueNotSaveable = (refused: Extract<ToolResult, { ok: false }>): ToolResult => ({
  ...refused,
  message:
    "Ikke vist brukeren: verdien brukeren selv ba om, kan ikke lagres. " +
    `Grunnen: ${refused.message} Bytt den IKKE stille ut med en annen verdi. Foreslår du en annen verdi, ` +
    "setter du kilden til ASSISTANT_SUGGESTION, og oppsummeringen sier hva brukeren ba om, hvorfor det ikke " +
    "kan lagres og hva kortet foreslår i stedet. Kortet viser brukeren grunnen.",
});

const weightsNotAddingUp = (sum: number): ToolResult => ({
  ok: false,
  deviationCode: "zz_FieldErrorCode_CriterionWeightsMustSumTo100",
  message:
    `Ikke vist brukeren: prosentvektene summerer til ${sum} %, og et kort med vekter som ikke summerer til 100 %, ` +
    "kan ikke bekreftes. Foreslå vekter som er mellom 1 og 100 % og summerer til 100 %, med kilde " +
    "ASSISTANT_SUGGESTION på vektene du har endret, og si i oppsummeringen hva brukeren ba om, hvorfor det ikke " +
    "kan lagres og hva du foreslår i stedet. Kan du ikke det uten å gjette, viser du ikke noe kort, men sier " +
    "hvorfor og spør brukeren hvilke vekter hun vil ha.",
});

const subCriterionWeightsNotAddingUp = (gaps: { label: string; unweighted: number; sum: number }[]): ToolResult => ({
  ok: false,
  deviationCode: "zz_FieldErrorCode_SubCriterionWeightsMustSumTo100",
  message:
    "Ikke vist brukeren: steget krever at underkriteriene under et kriterium med prosentvekt har hver sin " +
    "prosentvekt, og at de summerer til 100 % innenfor kriteriet. Her gjelder det " +
    gaps
      .map(({ label, unweighted, sum }) =>
        unweighted > 0 ? `«${label}» (${unweighted} uten vekt, resten ${sum} %)` : `«${label}» (${sum} %)`,
      )
      .join(", ") +
    ". Gi hvert underkriterium en vekt mellom 1 og 100 % slik at de summerer til 100 % innenfor kriteriet, med kilde " +
    "ASSISTANT_SUGGESTION på vektene du foreslår, og vis vektene i svaret. Vil du ikke foreslå vekter, sender du " +
    "kriteriet uten underkriterier. Si aldri at vektene summerer til 100 % før hver underkriterievekt gjør det.",
});

const closedAsRefused = (refused: Extract<ToolResult, { ok: false }>): ToolResult =>
  nothingSaved(refused, "Kortet viste brukeren at forslaget ikke kunne lagres. ");

const savedNothingIfRefused = (result: ToolResult): ToolResult =>
  result.ok || OUTCOME_UNKNOWN.test(result.deviationCode) ? result : nothingSaved(result);

const nothingSaved = (refused: Extract<ToolResult, { ok: false }>, shown = ""): ToolResult => ({
  ...refused,
  message: `${NOTHING_SAVED} ${shown}Grunnen: ${refused.message}`,
});

const OUTCOME_UNKNOWN = /^(5\d\d|FETCH_ERROR|TIMEOUT_ERROR|PARSING_ERROR|UNKNOWN)$/;

const readCall = async (
  input: { callName: string; args: Record<string, unknown>; label: string },
  deps: CallDeps,
): Promise<CallResult> => {
  const { callName, args, label } = input;
  const callKey = `${callName}:${JSON.stringify(args)}`;
  if (deps.seenCalls.has(callKey)) {
    return { result: { ok: true, data: { note: "Samme kall allerede utført." } }, outcome: null, breakOuter: false };
  }
  deps.seenCalls.add(callKey);
  deps.setActiveStep(label);
  const result = await runAndIndex(deps, callName, args);
  const highlight = navigableHighlight(highlightFromReadResult(result, deps.entityIndex), deps);
  deps.updateTurn((turn) => ({
    ...turn,
    steps: [...turn.steps, { label, ok: result.ok }],
    entities: addEntityHighlight(turn.entities ?? [], highlight),
  }));
  return { result, outcome: null, breakOuter: false };
};

// Dispatch one planner-requested call by kind: client-side navigation, a gated (confirmed) mutation,
// or a read. Memory bookkeeping (the tool reply, the SKIPPED balancing) is the loop's job — this
// Tool descriptions are planner-facing documentation and can run to a paragraph (updateContract
// enumerates every field); a status label is their first clause.
export const stepLabelFor = (callName: string): string =>
  CLIENT_TOOL_STEP_LABELS[callName] ??
  toolStepLabel(agentToolCatalog.find((tool) => tool.toolName === callName)?.description, callName);

const CLIENT_TOOL_STEP_LABELS: Record<string, string> = {
  navigate: "Åpner siden",
  [SEARCH_TOOL_NAME]: SEARCH_STEP_LABEL,
  [OPEN_DOCUMENT_TOOL_NAME]: OPEN_DOCUMENT_STEP_LABEL,
  [PROCUREMENT_RULES_TOOL_NAME]: PROCUREMENT_RULES_STEP_LABEL,
  [DRAFT_TOOL_NAME]: "Skriver et utkast",
};

export const toolStepLabel = (description: string | undefined, callName: string): string => {
  if (!description) {
    return callName;
  }
  const clauseEnd = description.search(/:|\.(?=\s+\p{Lu}|\s*$)/u);
  return clauseEnd === -1 ? description : description.slice(0, clauseEnd);
};

// only performs the call and reports its effect on the turn.
export const executeCall = async (
  requestedName: string,
  args: Record<string, unknown>,
  deps: CallDeps,
  plannerSummary: string | null = null,
): Promise<CallResult> => {
  const callName = currentToolName(requestedName);
  if (deps.withheldByAiSetting?.has(callName)) {
    deps.updateTurn((turn) => ({ ...turn, steps: [...turn.steps, { label: AI_NOT_ALLOWED_NOTE, ok: false }] }));
    return { result: WITHHELD_BY_AI_SETTING, outcome: null, breakOuter: false };
  }
  if (deps.enabledTools && !deps.enabledTools.has(callName)) {
    showModuleRefusal(deps);
    return { result: toolModuleNotIncluded, outcome: null, breakOuter: false };
  }
  const call = await routeCall(callName, args, deps, plannerSummary);
  if (!call.result.ok && call.result.deviationCode === TOOL_MODULE_NOT_INCLUDED) {
    showModuleRefusal(deps);
  }
  return call;
};

const routeCall = async (
  callName: string,
  args: Record<string, unknown>,
  deps: CallDeps,
  plannerSummary: string | null,
): Promise<CallResult> => {
  if (callName === "navigate") {
    return navigateCall(args, deps);
  }
  if (callName === SEARCH_TOOL_NAME) {
    return searchCall(args, deps);
  }
  if (callName === OPEN_DOCUMENT_TOOL_NAME) {
    return openDocumentCall(args, deps);
  }
  if (callName === PROCUREMENT_RULES_TOOL_NAME) {
    return procurementRulesCall(args, deps);
  }
  const descriptor = agentToolCatalog.find((tool) => tool.toolName === callName);
  const label = stepLabelFor(callName);
  const riskTier: RiskTier = descriptor?.riskTier ?? "none";
  if (descriptor && riskTier !== "none") {
    return gatedMutationCall({ callName, args, label, riskTier, descriptor, plannerSummary }, deps);
  }
  return readCall({ callName, args, label }, deps);
};

export const changesData = (callName: string): boolean =>
  (agentToolCatalog.find((tool) => tool.toolName === callName)?.riskTier ?? "none") !== "none";

const showModuleRefusal = (deps: CallDeps) =>
  deps.updateTurn((turn) => ({
    ...turn,
    steps: [...turn.steps, { label: TOOL_MODULE_NOT_INCLUDED_MESSAGE, ok: false }],
  }));
